import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TableHead, TableWrapper } from '../components/TableWrapper';
import { useToast } from '../contexts/ToastContext';
import {
  fortnoxOverviewApi,
  type FortnoxOverview,
  type FortnoxOverviewRow,
} from '../services/fortnoxOverview';

const moneyFormatter = new Intl.NumberFormat('sv-SE', {
  style: 'currency',
  currency: 'SEK',
  maximumFractionDigits: 0,
});

function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function yearStartDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-01-01`;
}

function formatMoney(value: number | null | undefined): string {
  return moneyFormatter.format(Number(value ?? 0));
}

function formatDate(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '-';
}

function KpiTile({ label, value, tone = 'default' }: { label: string; value: string; tone?: 'default' | 'good' | 'warn' }) {
  const toneClass = tone === 'good'
    ? 'border-success/35 bg-success/5'
    : tone === 'warn'
      ? 'border-info/60 bg-info/10'
      : 'border-gray-200 bg-white';
  return (
    <div className={`rounded border p-3 ${toneClass}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-1 text-lg font-semibold text-gray-950">{value}</div>
    </div>
  );
}

function InvoiceTable({ rows, emptyText }: { rows: FortnoxOverviewRow[]; emptyText: string }) {
  return (
    <TableWrapper maxHeight="360px">
      <TableHead>
        <tr>
          <th className="px-2 py-1.5 text-left font-medium">Nr</th>
          <th className="px-2 py-1.5 text-left font-medium">Datum</th>
          <th className="px-2 py-1.5 text-left font-medium">Namn</th>
          <th className="px-2 py-1.5 text-right font-medium">Total</th>
          <th className="px-2 py-1.5 text-right font-medium">Saldo</th>
        </tr>
      </TableHead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.documentNumber}-${row.date}-${row.name}`} className="border-b border-gray-100">
            <td className="px-2 py-1">{row.documentNumber || '-'}</td>
            <td className="px-2 py-1">{formatDate(row.date)}</td>
            <td className="px-2 py-1">{row.name || '-'}</td>
            <td className="px-2 py-1 text-right">{formatMoney(row.total)}</td>
            <td className="px-2 py-1 text-right">{formatMoney(row.balance)}</td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={5} className="px-2 py-3 text-sm text-gray-500">{emptyText}</td>
          </tr>
        )}
      </tbody>
    </TableWrapper>
  );
}

export function FortnoxOverviewPage() {
  const toast = useToast();
  const [from, setFrom] = useState(yearStartDate());
  const [to, setTo] = useState(todayDate());
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<FortnoxOverview | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fortnoxOverviewApi.get(from, to);
      setOverview(data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte hämta Fortnox-översikt');
    } finally {
      setLoading(false);
    }
  }, [from, to, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = overview?.summary;
  const accounting = overview?.accounting;
  const missingScopes = useMemo(() => {
    const granted = new Set((overview?.tokenScope ?? '').split(/\s+/).filter(Boolean));
    const required = ['invoice', 'payment', 'supplierinvoice', 'bookkeeping'];
    return required.filter((scope) => !granted.has(scope));
  }, [overview?.tokenScope]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-sm text-gray-500">
            <Link to="/office" className="hover:underline">Kontoret</Link> / Fortnox
          </div>
          <h1 className="text-lg font-semibold">Fortnox</h1>
        </div>
        <div className="flex flex-wrap items-end gap-2 text-sm">
          <label className="block">
            <span className="block text-xs text-gray-500">Från</span>
            <input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={from} onChange={(event) => setFrom(event.target.value)} className="h-9" />
          </label>
          <label className="block">
            <span className="block text-xs text-gray-500">Till</span>
            <input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={to} onChange={(event) => setTo(event.target.value)} className="h-9" />
          </label>
          <button type="button" onClick={() => void load()} disabled={loading} className="h-9 rounded border border-gray-300 px-3 hover:bg-gray-100 disabled:opacity-50">
            {loading ? 'Hämtar...' : 'Uppdatera'}
          </button>
        </div>
      </div>

      {!overview?.connected && !loading ? (
        <div className="rounded border border-info bg-info/10 p-3 text-sm">
          Fortnox är inte anslutet. <Link to="/settings?tab=fortnox" className="font-medium underline">Öppna Fortnox-inställningar</Link>
        </div>
      ) : null}

      {overview?.connected && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KpiTile label="Fakturerat" value={formatMoney(summary?.invoicedTotal)} tone="good" />
            <KpiTile label="Betalt" value={formatMoney(summary?.paidInPeriod)} />
            <KpiTile label="Kundfordringar" value={formatMoney(summary?.customerReceivables)} />
            <KpiTile label="Förfallet kund" value={formatMoney(summary?.customerOverdue)} tone={(summary?.customerOverdue ?? 0) > 0 ? 'warn' : 'default'} />
            <KpiTile label="Leverantörsfakturor" value={formatMoney(summary?.supplierInvoiceTotal)} />
            <KpiTile label="Leverantörsskuld" value={formatMoney(summary?.supplierDebt)} />
            <KpiTile label="Förfallet leverantör" value={formatMoney(summary?.supplierOverdue)} tone={(summary?.supplierOverdue ?? 0) > 0 ? 'warn' : 'default'} />
            <KpiTile label="Netto faktura/kostnad" value={formatMoney(summary?.estimatedCashResult)} tone={(summary?.estimatedCashResult ?? 0) >= 0 ? 'good' : 'warn'} />
          </div>

          <div className="space-y-2 rounded border border-gray-200 bg-white p-3 text-sm">
            <div className="font-medium">Status</div>
            <div><span className="text-gray-500">Period:</span> {formatDate(overview.from)} - {formatDate(overview.to)}</div>
            <div><span className="text-gray-500">Kundfakturor:</span> {summary?.invoiceCount ?? 0}</div>
            <div><span className="text-gray-500">Leverantörsfakturor:</span> {summary?.supplierInvoiceCount ?? 0}</div>
            <div><span className="text-gray-500">Token scopes:</span> {overview.tokenScope ?? '-'}</div>
            {missingScopes.length > 0 && (
              <div className="rounded border border-info bg-info/10 p-2 text-xs">
                Saknar scope i token: {missingScopes.join(', ')}. Spara scopes och logga in Fortnox igen.
              </div>
            )}
          </div>
        </div>
      )}

      {overview?.errors && overview.errors.length > 0 && (
        <div className="rounded border border-info bg-info/10 p-3 text-sm">
          <div className="font-medium">Fortnox-meddelanden</div>
          <ul className="mt-1 space-y-1">
            {overview.errors.map((error) => (
              <li key={`${error.section}-${error.message}`}>{error.section}: {error.message}</li>
            ))}
          </ul>
        </div>
      )}

      {overview?.connected && (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <KpiTile label="Bokförd omsättning 3xxx" value={formatMoney(accounting?.revenue)} />
            <KpiTile label="Bokförda kostnader 4xxx-8xxx" value={formatMoney(accounting?.costs)} />
            <KpiTile label="Bokfört resultat" value={formatMoney(accounting?.result)} tone={(accounting?.result ?? 0) >= 0 ? 'good' : 'warn'} />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <section className="space-y-2">
              <h2 className="font-medium">Kundfakturor</h2>
              <InvoiceTable rows={overview.recentInvoices ?? []} emptyText="Inga kundfakturor i perioden." />
            </section>
            <section className="space-y-2">
              <h2 className="font-medium">Leverantörsfakturor</h2>
              <InvoiceTable rows={overview.recentSupplierInvoices ?? []} emptyText="Inga leverantörsfakturor i perioden." />
            </section>
          </div>
        </>
      )}
    </div>
  );
}
